
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Attributes,
  Condition,
  Creature,
  CreatureGift,
  SENSES,
  Skill,
  SKILLS,
  Tool,
} from "@dndModels";
import { 
  CHARACTER_LEVELS,
  CR_OPTIONS, 
  CREATURE_CRS,
  DAMAGE_TYPES,
  PROFICIENCY_OPTIONS
} from "@dndModels/Constants";
import { AbilityScoreType } from "@dndModels/Types";

import DandDAPI from "@dndUtilities/system_api";
import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import StringBox from "@components/input/strings/StringBox";
import CenteredMenu from "@components/input/menus/CenteredMenu";
import DeleteButton from "@components/input/buttons/DeleteButton";
import CopyButton from "@components/input/buttons/CopyButton";
import AddButton from "@components/input/buttons/AddButton";
import CheckBox from "@components/input/CheckBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";

interface Props {
  obj: Creature | CreatureGift;
  disabled: boolean;
  onChange: () => void;
}

const api = DandDAPI.getInstance();

export default function CreatureAttributes({
  obj,
  disabled,
  onChange
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attributes_cr, setAttributesCR] = useState(obj.attributes_table.entries.length > 0 ? Math.min(...obj.attributes_table.entries.map(o => o.level)) : -1);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [display_mode, setDisplayMode] = useState("Attributes");
  const [add_at_level, setAddAtLevel] = useState("Next");
  const [trickle_up, setTrickleUp] = useState(false);
  const [shift_by_level, setShiftByLevel] = useState(0);
  const [edit_attribute, setEditAttribute] = useState("");
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["condition","tool"] });
    if (res) {
      setConditions(res.condition as Condition[]);
      setTools(res.tool as Tool[]);
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  useEffect(() => {
    if(reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  
  const cr_options = obj instanceof Creature ? CR_OPTIONS.filter(o => 
    +o >= obj.min_challenge_rating && +o <= obj.max_challenge_rating 
  ) : CHARACTER_LEVELS;
  const used_levels = obj.attributes_table.entries.map(o => o.level);

  const renderTable = () => {
    let level_options: number[] = [];
    if (obj instanceof Creature) {
      level_options = CR_OPTIONS.map(o => +o).filter(o => 
        o >= obj.min_challenge_rating && 
        o <= obj.max_challenge_rating
      );
    } else {
      level_options = CHARACTER_LEVELS.map(o => +o).filter(o => o < obj.max_level);
    }
    const min = level_options.length === 0 ? 0 : Math.min(...level_options);
    const max = level_options.length === 0 ? 0 : Math.max(...level_options);
    const empty_entries = level_options.map(o => +o).filter(o => o > min && o < max);
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 4
          }}>
          Entries&nbsp;
          <AddButton
            disabled={disabled}
            onClick={() => {
              const new_obj = obj;
              const entry = new Attributes();
              if (add_at_level === "Next") {
                const max_level = Math.max(...new_obj.attributes_table.entries.map(o => o.level), 0);
                entry.level = 1;
                const previous = new_obj.attributes_table.entries.find(o => o.level === max_level);
                if (previous) {
                  if (previous.level > 0.5) { // It's going to be a whole number, so just increment
                    entry.level = previous.level + 1;
                  } else {
                    const level_options = (new_obj instanceof Creature ? [...CREATURE_CRS] : [...CHARACTER_LEVELS]).map(o => +o).filter(o => o > previous.level);
                    entry.level = Math.min(...level_options);
                  }
                  entry.copy(previous, true);
                } 
              } else {
                entry.level = +add_at_level;
                const max_level = Math.max(...new_obj.attributes_table.entries.filter(o => o.level < entry.level).map(o => o.level), 0);
                const previous = new_obj.attributes_table.entries.find(o => o.level === max_level);
                if (previous) {
                  entry.copy(previous, true);
                } else {
                  // It's a new lowest.  Make it match the previous lowest, and remove trickle up.
                  const min_level = new_obj.attributes_table.entries.length === 0 ? 0 : Math.min(...new_obj.attributes_table.entries.map(o => o.level));
                  const lowest = new_obj.attributes_table.entries.find(o => o.level === min_level);
                  if (lowest) {
                    entry.copy(lowest, true);
                    setTrickleUp(false);
                  } 
                }
                setAddAtLevel("Next");
              }
              new_obj.attributes_table.entries.push(entry);
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Add at Level"
            options={level_options.filter(o => !used_levels.includes(o)).map(o => { return `${o}`; })}
            extra_options={["Next"]}
            value={add_at_level}
            disabled={disabled}
            onChange={setAddAtLevel}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <CheckBox
            name="Trickle Up"
            value={trickle_up}
            disabled={disabled}
            onChange={setTrickleUp}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <StringBox
            name="Shift by Level"
            value={`${shift_by_level}`}
            disabled={disabled}
            onBlur={(changed: string) => {
              setShiftByLevel(+changed);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Up"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.attributes_table.entries) {
                entry.level += shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Shift Down"
            disabled={disabled || shift_by_level === 0}
            onClick={() => {
              const new_obj = obj;
              for (const entry of new_obj.attributes_table.entries) {
                entry.level -= shift_by_level;
              }
              onChange();
              setShiftByLevel(0);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <ButtonBox
            name="Extrapolate Empty Entries"
            disabled={disabled || empty_entries.length === 0}
            onClick={() => {
              const new_obj = obj;
              new_obj.attributes_table.extrapolateEmptyFields();
              onChange();
              setPing(!ping);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row"
            className={cx2(rowClasses.root)}>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              Level
            </Grid>
            <Grid
              className='centered'
              size={{
                xs: 4
              }}>
              
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
            </Grid>
          </Grid>
        </Grid>
        { obj.attributes_table.entries.sort((a, b) => { return a.level - b.level; }).map(entry => {
          let options: string[] = []; 
          if (obj instanceof Creature) {
            options = CR_OPTIONS.map(o => +o).filter(o => 
              o >= obj.min_challenge_rating && 
              o <= obj.max_challenge_rating &&
              (o === entry.level || !used_levels.includes(o))
            ).map(o => { return `${o}`; });
          } else {
            options = CHARACTER_LEVELS.map(o => +o).filter(o => 
              o === entry.level || !used_levels.includes(o)).map(o => { return `${o}`; });
          }
          return (
            <Grid
              key={entry.true_id}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx2(rowClasses.root)}>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <SelectStringBox
                    name=""
                    options={options}
                    value={ `${entry.level}` }
                    onChange={(changed: string) => {
                      const new_obj = obj;
                      const new_cr = +changed;
                      const old_entry = new_obj.attributes_table.entries.find(o => o.level === entry.level);
                      if (old_entry) {
                        old_entry.level = new_cr;
                        onChange();
                      }
                      setAttributesCR(new_cr);
                      setReloading(true);
                    }}
                  />
                </Grid>
                <Grid
                  className="clickable centered"
                  onClick={() => {
                    setAttributesCR(entry.level);
                    setDisplayMode("Attributes");
                  }}
                  size={{
                    xs: 4
                  }}>
                  Select
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <CopyButton
                    onClick={() => {
                      const new_obj = obj;
                      const new_entry = entry.clone();
                      if (add_at_level === "Next") {
                        new_entry.level++;
                      } else {
                        new_entry.level = +add_at_level;
                      }
                      new_obj.attributes_table.entries.push(new_entry);
                      onChange();
                      setReloading(true);
                    }}
                  />
                  &nbsp;
                  <DeleteButton
                    onClick={() => {
                      const new_obj = obj;
                      new_obj.attributes_table.entries = new_obj.attributes_table.entries.filter(o => o.level !== entry.level);
                      onChange();
                      setReloading(true);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
        <Grid
          size={{
            xs: 12
          }}></Grid>
      </>
    );
  }

  const renderAttributes = () => {
    const use_entry = obj.attributes_table.entries.find(o => o.level === attributes_cr);
    if (use_entry) {
      if (edit_attribute === "speed") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Speed
            </Grid>
            { renderSpeeds(use_entry) }
          </Grid>
        );
      } else if (edit_attribute === "senses") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Senses
            </Grid>
            { renderSenses(use_entry) }
          </Grid>
        );
      } else if (edit_attribute === "condition_immunities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Condition Immunities
            </Grid>
            { conditions && conditions.filter(o => o.common).map(condition => {
              return renderCondition(use_entry, condition);
            })}
          </Grid>
        );
      } else if (edit_attribute === "immunities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Immunities
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 0);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Immunities"
                value={ use_entry.extra_damage_immunities }
                disabled={disabled}
                onBlur={(changed: string) => {
                  // const new_obj = obj;
                  use_entry.extra_damage_immunities = changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "resistances") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Resistances
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 0.5);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Resistances"
                value={ use_entry.extra_damage_resistances }
                disabled={disabled}
                onBlur={(changed: string) => {
                  // const new_obj = obj;
                  use_entry.extra_damage_resistances = changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "vulnerabilities") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Damage Vulnerabilities
            </Grid>
            { DAMAGE_TYPES.map(damage_type => {
              return renderDamageType(use_entry, damage_type, 2);
            })}
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Extra Damage Vulnerabilities"
                value={ use_entry.extra_damage_vulnerabilities }
                disabled={disabled}
                onBlur={(changed: string) => {
                  // const new_obj = obj;
                  use_entry.extra_damage_vulnerabilities = changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (edit_attribute === "tools") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Tools
            </Grid>
            { tools && tools.map(tool => {
              return renderTool(use_entry, tool);
            })}
          </Grid>
        );
      } else if (edit_attribute === "skills") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Skills
            </Grid>
            { SKILLS.sort((a,b) => a.name.localeCompare(b.name)).map(skill => {
              return renderSkill(use_entry, skill);
            })}
          </Grid>
        );
      } else if (edit_attribute === "saving_throws") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Saving Throws
            </Grid>
            { renderSavingThrow(use_entry, "STR") }
            { renderSavingThrow(use_entry, "DEX") }
            { renderSavingThrow(use_entry, "CON") }
            { renderSavingThrow(use_entry, "INT") }
            { renderSavingThrow(use_entry, "WIS") }
            { renderSavingThrow(use_entry, "CHA") }
          </Grid>
        );
      } else if (edit_attribute === "languages") {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              style={{ cursor: "pointer", fontWeight: "bold" }}
              onClick={() => {
                setEditAttribute("");
              }}
              size={{
                xs: 2
              }}>
              Back
            </Grid>
            <Grid
              size={{
                xs: 10
              }}>
              Languages
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Languages"
                value={use_entry.languages}
                disabled={disabled}
                onBlur={(changed: string) => {
                  // const new_obj = obj;
                  use_entry.languages = changed;
                  onChange();
                  setPing(!ping);
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="column" style={{ cursor: "pointer" }}>
            <Grid
              onClick={() => {
                setEditAttribute("speed");
              }}>
              <b>Speed</b> { use_entry.speed_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("senses");
              }}>
              <b>Senses</b> { use_entry.senses_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("immunities");
              }}>
              <b>Damage Immunities</b> { use_entry.immunities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("resistances");
              }}>
              <b>Damage Resistances</b> { use_entry.resistances_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("vulnerabilities");
              }}>
              <b>Damage Vulnerabilities</b> { use_entry.vulnerabilities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("condition_immunities");
              }}>
              <b>Condition Immunities</b> { use_entry.condition_immunities_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("tools");
              }}>
              <b>Tools</b> { use_entry.tools_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("skills");
              }}>
              <b>Skills</b> { use_entry.skills_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("saving_throws");
              }}>
              <b>Saving Throws</b> { use_entry.saving_throws_string }
            </Grid>
            <Grid
              onClick={() => {
                setEditAttribute("languages");
              }}>
              <b>Languages</b> { use_entry.languages }
            </Grid>
          </Grid>
        );
      }
    }
  }

  const renderSpeeds = (use_entry: Attributes) => {
    const return_me: any[] = [];
    return_me.push(renderSpeed(use_entry, "walk", "Walk (in feet)"));
    return_me.push(renderSpeed(use_entry, "swim", "Swim (in feet)"));
    return_me.push(renderSpeed(use_entry, "climb", "Climb (in feet)"));
    return_me.push(renderSpeed(use_entry, "fly", "Fly (in feet)"));
    return_me.push(renderSpeed(use_entry, "burrow", "Burrow (in feet)"));
    return_me.push(  
      <Grid
        key="hover"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name="Hover"
          value={ use_entry.hover }
          disabled={disabled}
          onToggle={() => {
            // const new_obj = obj;
            use_entry.hover = !use_entry.hover;
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
    return return_me;
  }

  const renderSpeed = (use_entry: Attributes, name: string, display: string) => {
    const speed = use_entry.speeds[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ display }
          value={ speed && speed > 0 ? `${speed}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            // const new_obj = obj;
            if (changed === "") {
              use_entry.speeds[name] = 0;
            } else {
              use_entry.speeds[name] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  const renderSenses = (use_entry: Attributes) => {
    const return_me: any[] = [];
    for (const sense of SENSES) {
      return_me.push(renderSense(use_entry, sense.name));
    }
    return return_me;
  }

  const renderSense = (use_entry: Attributes, name: string) => {
    const range = use_entry.senses[name];
    return (
      <Grid
        key={name}
        size={{
          xs: 4
        }}>
        <StringBox
          name={ `${name} (in feet)` }
          value={ range ? `${range}` : "" }
          type="number"
          disabled={disabled}
          onBlur={(changed: string) => {
            // const new_obj = obj;
            if (changed === "" || changed === "0") {
              delete use_entry.senses[name];
            } else {
              use_entry.senses[name] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderSkill = (use_entry: Attributes, skill: Skill) => {
    // if (skill.name === "Arcana") {
    //   // console.log(skill);
    //   // console.log(obj.skill_proficiencies2[skill._id]);
    // }
    return (
      <Grid
        key={skill._id}
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name={ skill.name }
          value={ use_entry.skill_profs[skill._id] ? `${use_entry.skill_profs[skill._id]}` : "0" }
          options={PROFICIENCY_OPTIONS}
          disabled={disabled}
          onChange={(changed: string) => {
            // const new_obj = obj;
            if (changed === "0") {
              delete use_entry.skill_profs[skill._id];
            } else {
              use_entry.skill_profs[skill._id] = +changed;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderSavingThrow = (use_entry: Attributes, ability_score: AbilityScoreType) => {
    return (
      <Grid
        size={{
          xs: 4
        }}>
        <CheckBox
          name={ ability_score }
          value={ use_entry.save_profs.includes(ability_score) }
          disabled={disabled}
          onChange={(changed: boolean) => {
            // const new_obj = obj;
            if (changed) {
              use_entry.save_profs.push(ability_score);
            } else {
              use_entry.save_profs = use_entry.save_profs.filter(o => o !== ability_score);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderTool = (use_entry: Attributes, tool: Tool) => {
    return (
      <Grid
        key={tool._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ tool.name }
          disabled={disabled}
          value={ use_entry.tool_profs.includes(tool.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.tool_profs.includes(tool.name)) {
              use_entry.tool_profs = use_entry.tool_profs.filter(o => o !== tool.name);
            } else {
              use_entry.tool_profs.push(tool.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderCondition = (use_entry: Attributes, condition: Condition) => {
    return (
      <Grid
        key={condition._id}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ condition.name }
          disabled={disabled}
          value={ use_entry.condition_immunities.includes(condition.name) }
          onToggle={() => {
            // const new_obj = obj;
            if (use_entry.condition_immunities.includes(condition.name)) {
              use_entry.condition_immunities = use_entry.condition_immunities.filter(o => o !== condition.name);
            } else {
              use_entry.condition_immunities.push(condition.name);
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const renderDamageType = (use_entry: Attributes, damage_type: string, multiplier: number) => {
    const damage_multipliers = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
    return (
      <Grid
        key={damage_type}
        container
        spacing={0}
        direction="row"
        size={{
          xs: 4
        }}>
        <ToggleButtonBox
          name={ damage_type }
          disabled={disabled}
          value={ damage_multipliers.includes(damage_type) }
          onToggle={() => {
            // const new_obj = obj;
            let damage_types = multiplier === 0 ? use_entry.damage_immunities : (multiplier === 0.5 ? use_entry.damage_resistances : use_entry.damage_vulnerabilities);
            if (damage_types.includes(damage_type)) {
              damage_types = damage_types.filter(o => o !== damage_type);
            } else {
              damage_types.push(damage_type);
            }
            if (multiplier === 0) {
              use_entry.damage_immunities = damage_types;
            } else if (multiplier === 0.5) {
              use_entry.damage_resistances = damage_types;
            } else {
              use_entry.damage_vulnerabilities = damage_types;
            }
            onChange();
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={["Attributes","Table"]}
            selected={display_mode}
            onSelect={setDisplayMode}
          />
        </Grid>
        { display_mode === "Attributes" ? 
          <>
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 12
                }}>
                <SelectStringBox 
                  name="Select CR for Attributes"
                  options={cr_options}
                  value={`${attributes_cr}`}
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    const new_cr = +changed;
                    if (new_obj.attributes_table.entries.length === 0) {
                      const entry = new Attributes();
                      entry.level = new_cr;
                      new_obj.attributes_table.entries.push(entry);
                    } else {
                      const old_entry = new_obj.attributes_table.entries.find(o => o.level === new_cr);
                      if (!old_entry && new_obj.attributes_table.entries.length > 0) {
                        const crs = new_obj.attributes_table.entries.map(o => o.level);
                        let copy_cr = Math.max(...crs.filter(o => o < new_cr), -1);
                        if (copy_cr === -1) {
                          copy_cr = Math.min(...crs.filter(o => o > new_cr), -1);
                        }
                        const entry = copy_cr === -1 ? new Attributes() : new_obj.attributes_table.entries.filter(o => o.level === copy_cr)[0].clone();
                        entry.level = new_cr;
                        new_obj.attributes_table.entries.push(entry);
                      }
                    }
                    onChange();
                    setAttributesCR(new_cr);
                    setReloading(true);
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12
                }}>{ attributes_cr > -1 && renderAttributes() }</Grid>
            </Grid>
          </>
        : renderTable() }
      </Grid>
    ); 
  }
}
